import { useEffect, useState } from "react";
import { getBackendVersion } from "@/lib/api";

// IMPORTANT: bump this in lockstep with backend BACKEND_PIPELINE_VERSION
// every time the render pipeline changes in a way users will perceive.
// The banner becomes visible whenever production's reported version differs.
const EXPECTED_PIPELINE_VERSION = "v11-cdn-cache-fix-veo3-2026-06-13";

/**
 * Site-wide banner that appears at the top of every page when the running
 * backend version doesn't match the frontend's expected pipeline version.
 *
 * This is the LAST RESORT signal to the user that they need to click
 * "Finish update → Deploy Now" — because every other clue (stuck renders,
 * tiny status badges, log lines) has been ignored for two days in a row.
 *
 * Self-hiding once production matches the frontend's expectation.
 */
export default function DeployStatusBanner() {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchVersion = () => {
      getBackendVersion()
        .then((data) => {
          if (!cancelled) setInfo(data);
        })
        .catch(() => {/* non-critical */});
    };
    fetchVersion();
    // Re-poll every 60s so the banner self-dismisses after the user
    // finishes the redeploy without needing a hard refresh.
    const id = setInterval(fetchVersion, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!info || dismissed) return null;
  const upToDate = info.pipeline_version === EXPECTED_PIPELINE_VERSION;
  if (upToDate) return null;

  return (
    <div
      data-testid="deploy-status-banner"
      className="sticky top-0 z-50 bg-red-600 text-white px-4 py-2 flex items-center gap-3 shadow-lg border-b-2 border-red-800"
    >
      <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold whitespace-nowrap">
        ⚠ DEPLOY MISMATCH
      </span>
      <span className="text-sm flex-1 min-w-0">
        <span className="hidden sm:inline">Production is running an older backend. </span>
        Renders will keep hanging until you click{" "}
        <span className="font-bold bg-white/20 px-2 py-0.5 rounded">Finish update → Deploy Now</span>{" "}
        in the top-right of your browser.
        <span className="hidden md:inline ml-2 font-mono text-[10px] opacity-70">
          running: {info.pipeline_version} · expected: {EXPECTED_PIPELINE_VERSION}
        </span>
      </span>
      <button
        data-testid="deploy-banner-dismiss"
        onClick={() => setDismissed(true)}
        className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/80 hover:text-white border border-white/40 px-2 py-0.5 rounded"
        title="Hide this banner for this session"
      >
        dismiss
      </button>
    </div>
  );
}
